import { produce } from "immer";
import { assign, fromPromise, setup } from "xstate";

import { generateShortUuid } from "@moment/api-collab/id";
import { Logger } from "@moment/logging";

import { getDocumentDirPath, mkdir } from "~/data/filesystem";
import { filesystemApi } from "~/state/api/filesystem";
import { sqliteApi } from "~/state/api/sqlite";
import type { DocumentListItem, DocumentMetadata } from "~/state/types";

import { initRepoMachine } from "../git/init-repo.machine";
import { createPageMachine } from "../pages/create-page.machine";

const logger = new Logger("createDocumentMachine");

export const createDocumentMachine = setup({
	types: {
		input: {} as {
			title?: string;
			body?: string;
			bodyType?: "html" | "text" | undefined;
		},
		context: {} as {
			documentId: string;
			directoryPath?: string;
			documentMetadata: DocumentMetadata;
			body: string | undefined;
			bodyType: "html" | "text" | undefined;
		},
		output: {} as DocumentListItem,
	},
	actors: {
		createDirectory: fromPromise<string, { documentId: string }>(async ({ input }) => {
			const directoryPath = await getDocumentDirPath(input.documentId);
			await mkdir(directoryPath, {
				recursive: true,
			});

			return directoryPath;
		}),
		createPage: createPageMachine,
		createMetadataFile: fromPromise<void, { path: string; contents: DocumentMetadata }>(
			async ({ input }) => {
				await filesystemApi.document.metadata.write(input.path, input.contents);
			}
		),
		initRepo: initRepoMachine,
		updateCache: fromPromise<void, DocumentListItem>(async ({ input }) => {
			await sqliteApi.documents.add(input);
		}),
	},
}).createMachine({
	id: "create-document",
	context: ({ input }) => {
		const documentId = generateShortUuid();

		return {
			documentId,
			documentMetadata: {
				id: documentId,
				title: input.title ?? "",
				created: new Date(),
				pagesMetadata: [], // Leaving pages array empty to start, we will add a stub page later in this flow
				schemaVersion: 1,
			},
			body: input.body,
			bodyType: input.bodyType,
		};
	},
	initial: "creatingDirectory",
	states: {
		creatingDirectory: {
			invoke: {
				src: "createDirectory",
				input: ({ context }) => ({ documentId: context.documentId }),
				onError: {
					actions: ({ event }) => {
						logger.error("Failed to create directory", {
							event,
						});
						throw new Error("Failed to create directory", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "creatingPage",
					actions: assign(({ context, event }) =>
						produce(context, (draft) => {
							draft.directoryPath = event.output;
						})
					),
				},
			},
		},
		creatingPage: {
			invoke: {
				src: "createPage",
				input: ({ context }) => ({
					documentId: context.documentId,
					documentPath: context.directoryPath!,
					pageMetadata: {
						slug: "index",
						title: context.documentMetadata.title ?? "",
					},
					body: context.body,
					bodyType: context.bodyType,
				}),
				onError: {
					actions: ({ event }) => {
						logger.error("Failed to create page", {
							event,
						});
						throw new Error("Failed to create page", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "creatingMetadataFile",
					actions: assign(({ context, event }) =>
						produce(context, (draft) => {
							draft.documentMetadata.pagesMetadata.push(event.output.pageMetadata);

							return draft;
						})
					),
				},
			},
		},
		creatingMetadataFile: {
			invoke: {
				src: "createMetadataFile",
				input: ({ context }) => ({
					path: context.directoryPath!,
					contents: context.documentMetadata,
				}),
				onError: {
					actions: ({ event }) => {
						logger.error("Failed to create metadata file", {
							event,
						});
						throw new Error("Failed to create metadata file", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "initializingGitRepository",
				},
			},
		},
		initializingGitRepository: {
			invoke: {
				src: "initRepo",
				input: ({ context }) => ({ path: context.directoryPath! }),
				onError: {
					actions: ({ event }) => {
						throw new Error("Failed to initialize git repository", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "done",
				},
			},
		},
		// TODO: this is not being called. bug? --Linnea
		updatingCache: {
			invoke: {
				src: "updateCache",
				input: ({ context }) => ({
					id: context.documentId,
					path: context.directoryPath!,
					title: context.documentMetadata.title,
					createdAt: context.documentMetadata.created,
					updatedAt: context.documentMetadata.created,
					workspaces: [],
				}),
				onError: {
					actions: ({ event }) => {
						throw new Error("Failed to update cache", {
							cause: event.error,
						});
					},
					target: "done",
				},
				onDone: {
					target: "done",
				},
			},
		},
		done: {
			type: "final",
		},
	},
	output: ({ context }) =>
		({
			id: context.documentId,
			path: context.directoryPath!,
			title: context.documentMetadata.title,
			createdAt: context.documentMetadata.created,
			updatedAt: context.documentMetadata.created,
			workspaces: [],
		}) as DocumentListItem,
});
